//@flow
import React from 'react';
import Formsy from 'formsy-react';
import formData from 'form-data-to-object';
import LoginFormHeader from '../components/LoginFormHeader.react';
import HowDidYouHear from '../components/HowDidYouHear.react';
import LoginFormName from '../components/LoginFormName.react';
import LoginFormEmailPassword from '../components/LoginFormEmailPassword.react';
import FacebookButton from './FacebookButton.react';
import ModalActions from '../../Actions/modal';
import CreateAccountModalActions from '../../Actions/createAccountModal';
import CustomerActions from '../../Actions/customer';
import SessionActions from '../../Actions/session';
import Constants from '../../constants';
import {browserHistory} from 'react-router';
import { toggleFreezeBody } from '../../Utils/device';
import UrlData from '../../Utils/url-data';

type Props = {
  initialNewUser:boolean,
  modal:boolean,
  unauthorized?:boolean,
  resetPasswordSuccess?:boolean,
  defaultEmail?:?string,
  isExistingUserEmail?:boolean,
  isGhostAccountEmail?:boolean,
  inReferral?:boolean,
  hideHDYHAU?:boolean,
  hideSignInOptions?:boolean,
  emailValidation?:(email:string)=>?string,
  emailDomainValidationError?:string,
  title?:string,
  subtitle?:string,
  emailPlaceholder?:string,
  passwordPlaceholder?:string,
};

type State = {
  newUser:boolean,
  canSubmit:boolean,
  formData:Object,
  emailPrompt:boolean,
};

// TODO: maybe move this to a prop
const hideFacebook = UrlData.getShouldHideFacebookAuth();

export default class LoginForm extends React.Component<Props, State> {
  form:?Formsy.Form;

  constructor(props:Props) {
    super(props);
    this.state = {
      newUser: this.props.initialNewUser,
      canSubmit: false,
      formData: {},
      emailPrompt: false,
    };
  }

  componentDidMount() {
    if (!this.props.inReferral) {
      toggleFreezeBody();
    }
  }

  componentWillUnmount() {
    if (!this.props.inReferral) {
      toggleFreezeBody();
    }
  }

  enableButton = () => {
    this.setState({
      canSubmit: true,
    });
  };

  disableButton = () => {
    this.setState({
      canSubmit: false,
    });
  };

  setValues = (currentValues:string) => {
    this.removeEmailPrompt();
    this.setState({ formData: formData.toObj(currentValues) });
  };

  removeEmailPrompt() {
    if (this.state.emailPrompt) {
      this.setEmailPrompt(false);
    }
  }

  loginOrCreateCustomer = (form:Object) => {
    // Check if additional emailValidation checks
    // are passed as props. Currently this only
    // happens from MembershipPromotionApp where we
    // need to check that email address has correct domain
    const {emailValidation} = this.props;
    if (emailValidation) {
      const error = emailValidation(form.email);
      if (error) {
        CustomerActions.setEmailDomainValidationError(error);
        return;
      }
    }
    CustomerActions.removeCustomerEmail();
    if (this.state.newUser) {
      this.createCustomer(form);
    } else {
      this.logInUser(form);
    }
  };

  logInUser = (form:Object) => {
    SessionActions.loginWithUsernamePassword(form.email, form.password);
  };

  createCustomer(form:Object) {
    let inputVal;
    if (form.howDidYouHearInput) inputVal = "Other - " + form.howDidYouHearInput;
    const customerData = {
      firstname: form.firstname,
      lastname: form.lastname,
      email: form.email,
      password: form.password,
      how_heard_about_nb: inputVal || form.howDidYouHearSelect,
      send_set_email: true,
    };

    CustomerActions.createCustomer(customerData);
  }

  showCreateAccountModal() {
    CustomerActions.removeExistingEmailError();
    ModalActions.closeModal(Constants.LOGIN_MODAL);
    CreateAccountModalActions.openCreateAccountModal();
  }

  showLoginModal() {
    CreateAccountModalActions.closeCreateAccountModal();
    ModalActions.openModal(Constants.LOGIN_MODAL);
  }

  showCreateAccountForm() {
    this.resetForm();
    this.setNewUser(true);
  }

  showLoginForm() {
    this.resetForm();
    this.setNewUser(false);
  }

  saveFormElement = (form:?React$ElementRef<typeof Formsy.Form>) => {
    this.form = form;
  };

  resetForm() {
    this.removeEmailPrompt();
    CustomerActions.removeExistingEmailError();
    this.form && this.form.reset();
  }

  displayCreateAccount = () => {
    SessionActions.removeLoginError();
    CreateAccountModalActions.collapseCreateAccountModal();
    if (this.props.modal) {
      this.showCreateAccountModal();
    } else {
      this.showCreateAccountForm();
    }
  };

  displayLogin = () => {
    SessionActions.removeLoginError();
    if (this.props.modal) {
      this.showLoginModal();
    } else {
      this.showLoginForm();
    }
  };

  openMagicLinkConfirmation = () => {
    if (!this.props.modal) return browserHistory.push('/signinlink');
    ModalActions.openModal(Constants.MAGIC_LINK_MODAL);
    ModalActions.closeModal(Constants.LOGIN_MODAL);
  }

  setNewUser(bool:boolean) {
    this.setState({
      newUser: bool,
    });
  }

  setEmailPrompt(bool:boolean) {
    this.setState({
      emailPrompt: bool,
    });
  }

  openEmailConfirmation() {
    if (this.props.modal || this.props.inReferral) {
      ModalActions.closeModal(Constants.LOGIN_MODAL);
      ModalActions.openModal(Constants.RESET_EMAIL_SENT_MODAL);
    }
  }

  sendPasswordResetEmail = () => {
    const email = this.state.formData.email || this.props.defaultEmail;
    const emailInput = this.form && this.form.inputs.find(input => input.props.name === 'email');
    const isValid = emailInput && emailInput.isValid();

    if (isValid) {
      CustomerActions.sendPasswordResetEmail(email);
      this.openEmailConfirmation();
    } else {
      this.setEmailPrompt(true);
    }
  }

  _shouldRenderEmailPrompt() {
    return !this.state.newUser && this.state.emailPrompt && !this.state.formData.email;
  }

  renderPasswordResetLink() {
    let className = "footer";
    if (this.state.emailPrompt) className = "footer with-email-notification";
    if (this.renderLoginError()) className = "footer with-error";
    if (!this.state.newUser) {
      return (<div className={className}>Forgot password? <a onClick={this.sendPasswordResetEmail}>Send reset email</a></div>);
    }
  }

  renderHowDidYouHear() {
    if (this.props.hideHDYHAU) {
      return null;
    }
    if (this.state.newUser && !this.props.inReferral) {
      return (
        <div className="row">
          <HowDidYouHear
            newUser={this.state.newUser}
            formData={this.state.formData}
          />
        </div>
      );
    }
  }

  renderLoginFormName() {
    if (this.state.newUser) {
      return (
        <LoginFormName
          newUser={this.state.newUser}
          formData={this.state.formData}
        />
      );
    }
  }

  renderLoginFormEmailPassword() {
    return (
      <LoginFormEmailPassword
        newUser={this.state.newUser}
        formData={this.state.formData}
        isExistingUserEmail={this.props.isExistingUserEmail}
        defaultEmail={this.props.defaultEmail}
        shouldRenderEmailPrompt={this._shouldRenderEmailPrompt()}
        emailDomainValidationError={this.props.emailDomainValidationError}
        emailPlaceholder={this.props.emailPlaceholder}
        placeholder={this.props.passwordPlaceholder}
      />
    );
  }

  renderButtonText() {
    if (this.props.inReferral) return 'Refer Friends';
    return this.state.newUser ? 'Sign up' : 'Sign in';
  }

  renderLoginError() {
    const className = 'errors';
    if (this.props.isGhostAccountEmail && this.state.newUser) {
      return (
        <div className={className}>
          <p>Your account already exists.</p>
          <p>We emailed you a link to set a password.</p>
        </div>
      );
    } else if (this.props.unauthorized || this.props.isGhostAccountEmail) {
      return <div className={className}>Your email or password was incorrect</div>;
    }
  }

  getFormClassName() {
    if (this.props.inReferral && this.state.newUser) return " create-account-referral";
    if (this.props.inReferral) return " referral";
    return "";
  }

  getDisabled() {
    return this.state.canSubmit ? "" : " inactive";
  }

  getLoginFormClass() {
    return this.props.inReferral ? " referral" : "";
  }

  getFacebookOption() {
    if (hideFacebook) return null;
    return (
      <div className="facebook-option">
        <FacebookButton />
      </div>
    );
  }

  getMagicLinkOption() {
    if (this.state.newUser || this.props.resetPasswordSuccess) return null;
    return (
      <div className="magiclink-option">
        <button
          className="cta-secondary-color magiclink-button"
          onClick={this.openMagicLinkConfirmation}
        >
          Sign in with an email link
        </button>
        <div className="magiclink-desc">
          Don’t know your password? That’s fine, we can email you a link that will sign you in automatically.
        </div>
      </div>
    );
  }

  renderLoginOptions() {
    if (this.props.hideSignInOptions) {
      return null;
    }
    return (
      <div className="login-options">
        {this.getFacebookOption()}
        {this.getMagicLinkOption()}
        or
      </div>
    );
  }

  render() {
    return (
      <div className={"login-form-container" + this.getFormClassName()}>
        <LoginFormHeader
          newUser={this.state.newUser}
          displayLogin={this.displayLogin}
          displayCreateAccount={this.displayCreateAccount}
          resetPasswordSuccess={this.props.resetPasswordSuccess}
          inReferral={this.props.inReferral}
          hideSignInOptions={this.props.hideSignInOptions}
          title={this.props.title}
          subtitle={this.props.subtitle}
        />
        {this.renderLoginOptions()}
        <Formsy.Form
          className={"formsy-form login-form" + this.getLoginFormClass()}
          ref={this.saveFormElement}
          onValidSubmit={this.loginOrCreateCustomer}
          onValid={this.enableButton}
          onInvalid={this.disableButton}
          onChange={this.setValues}
        >
          {this.renderLoginFormName()}
          {this.renderLoginFormEmailPassword()}
          {this.renderLoginError()}
          {this.renderPasswordResetLink()}
          {this.renderHowDidYouHear()}
          <button
            className={"login-button cta-large" + this.getDisabled()}
            id="auth-submit"
            disabled={!this.state.canSubmit}
          >
            {this.renderButtonText()}
          </button>
        </Formsy.Form>
      </div>
    );
  }
}
